import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { toast } from "@/shared/lib/toast";

import { useActivateLinkedGroup } from "../api/use-activate-linked-group";
import { useLinkedGroup } from "../api/use-linked-group";
import { usePrecheckActivation } from "../api/use-precheck-activation";
import { useTransitionStatus } from "../api/use-transition-status";
import {
  activationReasonsFromError,
  formatActivationReasons,
  type ActivationReason,
} from "../lib/activation-reasons";

import { PrimaryButton } from "./buttons";

interface ActivatePanelProps {
  promotionId: string;
  /** Fired after a successful draft→active transition. */
  onActivated?: () => void;
  /** Fired with the precheck reasons when activation is blocked (page maps them to fields). */
  onBlocked?: (reasons: ActivationReason[]) => void;
  /**
   * Persists the current builder edits BEFORE precheck so activation acts on the
   * user's unsaved rules, not stale server state. Resolves `false` when the save
   * was blocked/errored (the builder toasted why) → activation aborts.
   */
  onBeforeActivate?: () => Promise<boolean>;
  /**
   * Linked-group id registered by the builder (`onRegisterActivateGroup`, Task 11)
   * when this promotion belongs to a multi-channel draft group with >1 member.
   * Null/undefined → standalone single-promotion activation (the original flow
   * below). Present → the button switches to "Activate for N channels" and calls
   * `useActivateLinkedGroup` (the BE prechecks every member itself, all-or-nothing).
   */
  groupId?: string | null;
  /** True while the builder's own channel add/remove mutations are in flight. */
  groupDisabled?: boolean;
}

/**
 * Activation control (spec §5.4 / §6 ActivateToggle). Activation is the only
 * draft→active transition and is precheck-gated: we GET the dry-run precheck
 * first; if it returns reasons we surface them and block; if ok we POST the
 * `active` transition. (Take-active-down is `finish`, handled elsewhere — the
 * BE matrix has no active→draft.)
 */
export function ActivatePanel({
  promotionId,
  onActivated,
  onBlocked,
  onBeforeActivate,
  groupId,
  groupDisabled,
}: ActivatePanelProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const [armed, setArmed] = useState(false);
  const precheck = usePrecheckActivation(promotionId, armed);
  const transition = useTransitionStatus();
  const [reasons, setReasons] = useState<ActivationReason[] | null>(null);

  // Shares the builder's `useLinkedGroup(groupId)` query cache (same key) — no
  // extra network round trip in practice — purely to derive the member count for
  // the "Activate for N channels" label.
  const linkedGroup = useLinkedGroup(groupId ?? null);
  const activateGroup = useActivateLinkedGroup();
  const groupMemberCount = linkedGroup.data?.items.length ?? 0;
  const isGroupActivate = Boolean(groupId) && groupMemberCount > 1;

  const busy = precheck.isFetching || transition.isPending || activateGroup.isPending;

  async function handleActivate(): Promise<void> {
    setReasons(null);
    // Save-first: persist current builder edits so precheck sees the just-saved
    // rules. A blocked/errored save (false) aborts — its own toast explained why.
    const ok = onBeforeActivate ? await onBeforeActivate() : true;
    if (!ok) return;

    if (isGroupActivate && groupId) {
      // Group activation is all-or-nothing: the BE prechecks every draft member
      // itself before activating any of them, so there's no separate dry-run here.
      activateGroup.mutate(groupId, {
        onSuccess: () => {
          toast.success(t("precheck.activated"));
          onActivated?.();
        },
        onError: (err) => {
          const groupReasons = activationReasonsFromError(err);
          if (groupReasons) {
            setReasons(groupReasons);
            onBlocked?.(groupReasons);
            return;
          }
          toast.error(apiErrorMessage(err));
        },
      });
      return;
    }

    setArmed(true);
    const result = await precheck.refetch();
    const data = result.data;
    if (!data) return;
    if (!data.ok) {
      setReasons(data.reasons);
      onBlocked?.(data.reasons);
      return;
    }
    transition.mutate(
      { id: promotionId, target: "active" },
      {
        onSuccess: () => {
          toast.success(t("precheck.activated"));
          onActivated?.();
        },
        onError: (err) => toast.error(apiErrorMessage(err)),
      },
    );
  }

  return (
    <div data-testid="activate-panel" className="space-y-3">
      <PrimaryButton
        type="button"
        data-testid="activate-button"
        disabled={busy || Boolean(groupDisabled)}
        onClick={() => void handleActivate()}
      >
        <CheckCircle2 className="h-4 w-4" aria-hidden />
        {busy
          ? t("precheck.checking")
          : isGroupActivate
            ? t("builder.activate_for_n_channels", { count: groupMemberCount })
            : t("precheck.activate")}
      </PrimaryButton>

      {reasons && reasons.length > 0 && (
        <div
          data-testid="activate-reasons"
          className="rounded-xl border border-[var(--intent-danger-border)] bg-[var(--intent-danger-bg)] p-3"
        >
          <p className="mb-2 flex items-center gap-1.5 text-[13px] font-bold text-[var(--intent-danger-fg)]">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            {t("precheck.blocked")}
          </p>
          <ul className="space-y-1">
            {formatActivationReasons(reasons, t).map((line, i) => (
              <li
                key={i}
                data-testid={`activate-reason-${i}`}
                className="text-[13px] text-[var(--intent-danger-fg)]"
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
